import { PROPOSAL_ACCENT, PROPOSAL_BG, PROPOSAL_COPY_FONT, PROPOSAL_TEXT } from "@/lib/proposalTheme";
import type { ProposalIntroSettings } from "@shared/proposalIntroSettings";
import { cn } from "@/lib/utils";

type Props = {
  content: ProposalIntroSettings;
  className?: string;
};

/** Fixed luxury magazine layout — content only; structure is not editable. */
export default function ProposalIntroMagazineContent({ content, className }: Props) {
  const { hero, about, statistics, globalExperience, services, homeownerGuide } = content;

  return (
    <div
      className={cn("space-y-10 sm:space-y-14", className)}
      data-testid="proposal-intro-magazine"
      style={{ fontFamily: PROPOSAL_COPY_FONT, color: PROPOSAL_TEXT }}
    >
      <section className="overflow-hidden rounded-2xl border border-[#1A2744]/10 bg-white">
        <div className="relative min-h-[280px] sm:min-h-[360px]">
          {hero.imageUrl ? (
            <img
              src={hero.imageUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg, ${PROPOSAL_TEXT} 0%, #3d5a80 55%, ${PROPOSAL_ACCENT}33 100%)`,
              }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A2744]/88 via-[#1A2744]/45 to-transparent" />
          <div className="relative flex h-full min-h-[280px] flex-col justify-end p-6 sm:min-h-[360px] sm:p-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/75">Clarity</p>
            <h2 className="mt-3 max-w-3xl text-2xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
              {hero.headline}
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-white/90 sm:text-base">{hero.subheadline}</p>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[#1A2744]/10 bg-white p-6 sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: PROPOSAL_ACCENT }}>
          {about.label}
        </p>
        <h3 className="mt-3 text-xl font-semibold tracking-tight sm:text-2xl">{about.title}</h3>
        <div className="mt-4 space-y-4 text-sm leading-8 text-[#1A2744]/90 sm:text-base">
          {about.body.split(/\n\s*\n/).map((paragraph) => (
            <p key={paragraph.slice(0, 40)}>{paragraph}</p>
          ))}
        </div>
      </section>

      <section
        className="grid gap-px overflow-hidden rounded-2xl border border-[#1A2744]/10 sm:grid-cols-2 lg:grid-cols-4"
        style={{ backgroundColor: `${PROPOSAL_TEXT}14` }}
      >
        {statistics.map((stat) => (
          <div key={stat.label} className="flex flex-col justify-between bg-white p-6 sm:p-7">
            <p className="text-3xl font-semibold tracking-tight sm:text-4xl" style={{ color: PROPOSAL_TEXT }}>
              {stat.value}
            </p>
            <div className="mt-6">
              <p className="text-sm font-semibold uppercase tracking-[0.14em]">{stat.label}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.12em] text-[#1A2744]/65">{stat.supportingText}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-[#1A2744]/10 p-6 sm:p-8" style={{ backgroundColor: PROPOSAL_BG }}>
        <h3 className="text-xl font-semibold tracking-tight sm:text-2xl">{globalExperience.title}</h3>
        <div className="mt-4 space-y-4 text-sm leading-8 text-[#1A2744]/90 sm:text-base">
          {globalExperience.body.split(/\n\s*\n/).map((paragraph) => (
            <p key={paragraph.slice(0, 40)}>{paragraph}</p>
          ))}
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2">
        {services.map((service) => (
          <article
            key={service.title}
            className="overflow-hidden rounded-2xl border border-[#1A2744]/10 bg-white"
          >
            <div className="aspect-[16/10] w-full bg-[#1A2744]/5">
              {service.imageUrl ? (
                <img src={service.imageUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div
                  className="flex h-full w-full items-end p-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#1A2744]/35"
                >
                  {service.title}
                </div>
              )}
            </div>
            <div className="p-5 sm:p-6">
              <h4 className="text-base font-semibold tracking-tight sm:text-lg">{service.title}</h4>
              <p className="mt-3 text-sm leading-7 text-[#1A2744]/88">{service.description}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#1A2744]/10 bg-white">
        <div className="grid md:grid-cols-[220px_1fr]">
          <div className="aspect-[3/4] bg-[#1A2744]/5 md:aspect-auto md:min-h-full">
            {homeownerGuide.coverImageUrl ? (
              <img src={homeownerGuide.coverImageUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full min-h-[220px] items-center justify-center p-6 text-center text-xs font-semibold uppercase tracking-[0.18em] text-[#1A2744]/35">
                Homeowner Guide
              </div>
            )}
          </div>
          <div className="p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: PROPOSAL_ACCENT }}>
              Homeowner Guide
            </p>
            <h3 className="mt-3 text-xl font-semibold tracking-tight">{homeownerGuide.title}</h3>
            <p className="mt-4 text-sm leading-7 text-[#1A2744]/88">{homeownerGuide.description}</p>
            <ul className="mt-5 space-y-2 text-sm leading-7 text-[#1A2744]/88">
              {homeownerGuide.bullets.map((bullet) => (
                <li key={bullet} className="flex gap-2">
                  <span style={{ color: PROPOSAL_ACCENT }} aria-hidden>
                    —
                  </span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
            {homeownerGuide.pdfUrl ? (
              <a
                href={homeownerGuide.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: PROPOSAL_TEXT }}
              >
                {homeownerGuide.ctaText}
              </a>
            ) : (
              <span
                className="mt-6 inline-flex items-center rounded-lg px-5 py-2.5 text-sm font-semibold text-white/70"
                style={{ backgroundColor: PROPOSAL_TEXT }}
              >
                {homeownerGuide.ctaText}
              </span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
